import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowLeft, Home } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Password reset successful");
      navigate("/login");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="w-full max-w-md">
        <div className="flex items-center justify-between">
          <BrandLogo />
          <Link to="/" data-testid="home-button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 transition-colors hover:text-amber-600"><Home className="h-4 w-4" /> Home</Link>
        </div>
        <h1 className="mt-8 font-display text-3xl font-bold text-slate-900">Set a new password</h1>
        <p className="mt-1 text-slate-500">Choose a strong password for your account.</p>

        {!token ? (
          <div className="mt-6 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="reset-no-token">
            This reset link is missing its token. Please use the link from your email, or request a new one.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div>
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" className="mt-1.5" data-testid="reset-password-input" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required placeholder="••••••••" className="mt-1.5" data-testid="reset-confirm-input" />
            </div>
            {error && <p className="text-sm font-medium text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-button"
              className="w-full gap-2 bg-amber-600 py-6 text-base font-semibold hover:bg-amber-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Reset password
            </Button>
          </form>
        )}

        <Link to="/login" className="mt-6 flex items-center justify-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700" data-testid="reset-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
